import { type Database } from "@fitness-app/supabase";

import {
  type AddTagsToTraineeProfile,
  type EmailClientAutomationTask,
  type RemoveTagsFromTraineeProfile,
  type TaskCreator,
  type TaskType,
} from "./Automation";
import { type CalendarEventType } from "./CalendarEvent";
import { type Campaign } from "./Campaign";
import { type FileMessageContent } from "./Chat";
import {
  type CycleSurvey,
  type CycleSurveyType,
  type RecurringSurveyInterval,
  type SurveyAnswer,
  type SurveyIntervalUnit,
  type SurveyType,
} from "./ClientSurvey";
import { type EmailClient } from "./ProductsSettings";
import { type SurveyQuestion, type SurveyQuestionAutomation, type SurveyQuestionId } from "./SurveyTemplate";
import { type TeamMemberTaskCategory, type TeamMemberTaskPriority } from "./TeamMemberTask";
import { type TraineeStatus } from "./Trainee";
import { type UserRoleEnum } from "./UserProfile";

export type AutomationTemplateEntity = Database["public"]["Tables"]["automation_template"]["Row"];

export enum TemplateType {
  ScheduledMessages = "scheduled_messages",
  ScheduledEmails = "scheduled_emails",
  ScheduledSurvey = "scheduled_survey",
  ScheduledPopUp = "scheduled_pop_up",
  ScheduledPopUpFromAutomation = "scheduled_pop_up_from_automation",
  ScheduleProgramAutomation = "schedule_program_automation",
  ScheduledAiAgentMessage = "scheduled_ai_agent_message",
  ScheduledAiReport = "scheduled_ai_report",
  ScheduledAssignHabits = "scheduled_assign_habits",
  ScheduledEnrollChallenge = "scheduled_enroll_challenge",
}

/**
 * TRE-1609 — assigns habit templates to the client on schedule, exactly like a trainer
 * would from the admin panel. Idempotent per (habitTemplateId, traineeId): an already
 * existing client_habit row is left untouched (never resurrected or reset).
 */
export interface ScheduledAssignHabitsAutomation {
  type: TemplateType.ScheduledAssignHabits;
  id: string;
  name: string;
  /** habit_template ids to assign; each is skipped if the client already has it. */
  habitTemplateIds: string[];
  sentTime: SentTimeConfig & {
    hours: number;
    minutes: number;
  };
}

/**
 * TRE-1609 — enrolls the client into a challenge on schedule.
 * Exactly one of the two targets must be set:
 *  - challengeTemplateId — join the CURRENT edition (upcoming/active) of a recurring
 *    challenge template; the unique challenge_template_current_edition_uidx index
 *    guarantees at most one such edition exists.
 *  - challengeId — join one specific challenge instance.
 * Idempotent per (challengeId, traineeId): an existing participant row means skip.
 */
export interface ScheduledEnrollChallengeAutomation {
  type: TemplateType.ScheduledEnrollChallenge;
  id: string;
  name: string;
  challengeTemplateId?: string | null;
  challengeId?: string | null;
  sentTime: SentTimeConfig & {
    hours: number;
    minutes: number;
  };
}

/**
 * TRE-2072 — who the client sees as the author of a scheduled AI message / report.
 *  - trainer: the generated text lands in her regular chat as a message from the
 *    trainer (advisor_message_request outbox, delivered by automation-service).
 *  - advisor: the text OPENS a new client_advisor conversation (agent_chat_session)
 *    she can reply to (start-advisor-conversation-task). Requires the client's
 *    clientAdvisor flag (advisorEnabled automation); otherwise the executor falls
 *    back to the trainer path so she never gets silence.
 * Optional on purpose: tasks saved before TRE-2072 keep the trainer behaviour.
 */
export type AiAutomationTaskSender = "trainer" | "advisor";

export const DEFAULT_AI_AUTOMATION_TASK_SENDER: AiAutomationTaskSender = "trainer";

/**
 * TRE-2072 — one set of bounds for the AI message task, shared by the admin form,
 * the client-web start-conversation endpoint and start-advisor-conversation-task's
 * schema, so a task the admin lets you save can never be rejected by the task.
 */
export const AI_AUTOMATION_TASK_LIMITS = {
  promptTemplate: { min: 3, max: 4000 },
  tone: { max: 200 },
  maxLength: { min: 120, max: 2000, default: 600 },
  fallbackMessage: { max: 4000 },
} as const;

/**
 * TRE-1608 — schedules a PERSONALIZED advisor message instead of a fixed text.
 * The executor enqueues an advisor_message_request; a trigger.dev task renders the
 * message with the client-advisor agent (profile/progress-aware) and the delivery
 * step sends it through the regular chat pipeline. If generation fails after
 * retries, fallbackMessage (when set) is sent instead.
 */
export interface ScheduledAiAgentMessageAutomation {
  type: TemplateType.ScheduledAiAgentMessage;
  id: string;
  name: string;
  /** Content-team intent, e.g. "podsumuj tydzień, pochwal konkrety, przypomnij o ankiecie". */
  promptTemplate: string;
  /** Optional tone hint ("motywująco", "konkretnie"). */
  tone?: string | null;
  /** Soft cap in characters for the generated message. */
  maxLength?: number | null;
  /** Whether the advisor may weave in an offer (getOffers-grounded). */
  upsellHint?: boolean;
  /**
   * Static message sent when generation ultimately fails. Optional since TRE-2072:
   * without it a failed generation sends nothing and the task ends as failed
   * (visible in the admin) instead of the client getting a static text.
   */
  fallbackMessage?: string | null;
  /** TRE-2072 — trainer chat message (default) or a new advisor conversation. */
  sender?: AiAutomationTaskSender;
  sentTime: SentTimeConfig & {
    hours: number;
    minutes: number;
  };
  filters?: (TraineeTagFilterCondition | CalendarEventFilterCondition | CRMTagFilterCondition)[];
  filterOperator?: FilterOperator;
}

/**
 * TRE-2044 — schedules an AI REPORT for the client (her last N days: activity,
 * progress, chat context) written in the advisor voice. Rides the same outbox as
 * the advisor message (advisor_message_request, kind = 'report'): the trigger.dev
 * generator renders it with the trainee-report agent and the delivery step sends it
 * through the regular chat pipeline; fallbackMessage (when set) covers a failed generation.
 * Replaces the global Monday "weekly report" cron — the content team decides in
 * which program week/day a client gets her summary.
 */
export interface ScheduledAiReportAutomation {
  type: TemplateType.ScheduledAiReport;
  id: string;
  name: string;
  /** Lookback window of the report in days (one of traineeAiReportAllowedDays). */
  days: number;
  /** Static message sent when generation ultimately fails. Optional since TRE-2072 (see the message task). */
  fallbackMessage?: string | null;
  /** TRE-2072 — trainer chat message (default) or a new advisor conversation opened with the report. */
  sender?: AiAutomationTaskSender;
  sentTime: SentTimeConfig & {
    hours: number;
    minutes: number;
  };
  filters?: (TraineeTagFilterCondition | CalendarEventFilterCondition | CRMTagFilterCondition)[];
  filterOperator?: FilterOperator;
}

export interface CreateTemplateDTO {
  name: string;
  comment: string;
  type: TemplateType;
  messages: string[];
  authorId: string;
  authorRole: UserRoleEnum;
  authorName: string;
  sentTime: {
    hours: number;
    minutes: number;
  };
}

export interface BaseAutomationTemplate<Type extends TemplateType, Data extends object> extends Omit<
  AutomationTemplateEntity,
  "type" | "data"
> {
  id: string;
  name: string;
  comment: string;
  createdAt: string;
  updatedAt: string;
  type: Type;
  data: Data;
}

export interface ScheduledMessagesTemplateData {
  messages: string[];
  attachments: FileMessageContent[];
  authorId: string;
  authorRole: UserRoleEnum;
  authorName: string;
  sentTime: {
    hours: number;
    minutes: number;
  };
}

export type ScheduledMessagesTemplate = BaseAutomationTemplate<
  TemplateType.ScheduledMessages,
  ScheduledMessagesTemplateData
>;

export enum AutomationMessageType {
  OneTime = "one-time",
  Recurring = "recurring",
}

export type CalendarEventFilterCondition = {
  type: "calendarEvents";
  requiredTypes: CalendarEventType[];
  range: "today" | "thisWeek" | "thisMonth" | "thisYear" | "nextWeek" | "thisAndNextWeek";
  condition: "hasCalendarEvent" | "notHasCalendarEvent";
};

export type TraineeTagFilterCondition = {
  type: "tags";
  tags: string[];
  condition: "hasTag" | "notHasTag";
};

export type CRMTagFilterCondition = {
  type: "crmTags";
  tags: string[];
  emailClients: { client: EmailClient; account: string }[];
  condition: "hasTag" | "notHasTag";
};

export enum FilterOperator {
  OR = "OR",
  AND = "AND",
}

export interface SentTimeConfig {
  mode?: "absolute" | "relative";
  avoidSendingOnTheNight?: boolean;
  calculateFullRangeOverNight?: boolean;
}

export interface ScheduledMessagesAutomation {
  type: TemplateType.ScheduledMessages;
  id: string;
  name: string;
  templateId: string | null;
  comment?: string;
  attachments: FileMessageContent[];
  messages: string[];
  authorId: string;
  authorRole: UserRoleEnum;
  authorName: string;
  authorAvatar: string | null;
  sentTime: SentTimeConfig & {
    hours: number;
    minutes: number;
  };
  variables?: string[];
  sendToDefaultChannel?: boolean;
  cycleNumber?: number;
  filters?: (TraineeTagFilterCondition | CalendarEventFilterCondition | CRMTagFilterCondition)[];
  filterOperator?: FilterOperator;
  messageType?: AutomationMessageType;
  recurring?: {
    cycleType: CycleSurveyType;
    count: number;
    unit: SurveyIntervalUnit;
  } & CycleSurvey;
}

export interface ScheduledPopUpMessagesAutomation {
  type: TemplateType.ScheduledPopUp;
  id: string;
  name: string;
  templateId: string | null;
  comment?: string;
  attachments: FileMessageContent[];
  data: Campaign["data"];
  deadlineAt?: number | null;
  authorId: string;
  authorRole: UserRoleEnum;
  authorName: string;
  authorAvatar: string | null;
  sentTime: {
    hours: number;
    minutes: number;
  };
  variables?: string[];
  metadata: {
    actorUserId: string;
    tenantOwnerId: string;
    count: number;
    total: number;
    condition?: "hasTag" | "notHasTag" | null | false;
    tags?: string[] | null;
    products?: string[] | "all" | null;
    statuses?: TraineeStatus[] | null;
  };
  filters?: never;
}

export interface ScheduledPopUpMessagesAutomationFromAutomation {
  type: TemplateType.ScheduledPopUpFromAutomation;
  id: string;
  name: string;
  templateId: string | null;
  comment?: string;
  attachments: FileMessageContent[];
  data: Campaign["data"];
  authorId: string;
  authorRole: UserRoleEnum;
  authorName: string;
  authorAvatar: string | null;
  sentTime: SentTimeConfig & {
    hours: number;
    minutes: number;
  };
  variables?: string[];
  filters?: (TraineeTagFilterCondition | CalendarEventFilterCondition | CRMTagFilterCondition)[];
  filterOperator?: FilterOperator;
  metadata?: never;
}

export interface ScheduledEmailIntegrationAutomation extends Omit<EmailClientAutomationTask, "triggers"> {
  id: string;
  sentTime: SentTimeConfig & {
    hours: number;
    minutes: number;
  };
}

export interface ScheduledFitnessAppAutomation {
  id: string;
  type: TaskType.FitnessApp;
  action: AddTagsToTraineeProfile | RemoveTagsFromTraineeProfile;
  sentTime: SentTimeConfig & {
    hours: number;
    minutes: number;
  };
  active?: boolean;
  description?: string;
  protected?: boolean;
  creator?: TaskCreator;
}

export interface ScheduledTeamMemberTaskAutomation {
  id: string;
  type: TaskType.TeamMemberTask;
  title: string;
  description?: string;
  priority: TeamMemberTaskPriority;
  category: TeamMemberTaskCategory;
  assignedTeamMemberId: string;
  assignedTeamMemberName: string;
  assignedTeamMemberAvatar?: string | null;
  sentTime: SentTimeConfig & {
    hours: number;
    minutes: number;
  };
  active?: boolean;
  protected?: boolean;
  creator?: TaskCreator;
}

export interface BaseSurveyTemplate {
  id: string;
  name: string;
  description: string | null;
  templateId: string | null;
  title: string;
  editable: boolean;
  comment?: string;
  answers: Record<string, SurveyAnswer> | null;
  type: SurveyType;
  variables: Record<string, string | number> | null;
  automation: Record<SurveyQuestionId, SurveyQuestionAutomation[]> | null;
  questions: Record<string, SurveyQuestion>;
}

export interface OneTimeSurveyTemplate extends BaseSurveyTemplate {
  type: SurveyType.OneTime;
  interval: null;
}

export interface RecurringSurveyTemplate extends BaseSurveyTemplate {
  type: SurveyType.Recurring;
  interval: RecurringSurveyInterval;
  cycleNumber: number;
}

export interface ScheduledSurveyAutomation {
  type: TemplateType.ScheduledSurvey;
  id: string;
  name: string;
  templateId: string | null;
  comment?: string;
  survey: OneTimeSurveyTemplate | RecurringSurveyTemplate;
  sentTime: {
    hours: number;
    minutes: number;
    offset: number;
    timezone: string;
    mode?: "absolute" | "relative";
    avoidSendingOnTheNight?: boolean;
    calculateFullRangeOverNight?: boolean;
  };
  cycleNumber?: number;
  source?: never;
  filters?: (TraineeTagFilterCondition | CalendarEventFilterCondition | CRMTagFilterCondition)[];
  filterOperator?: FilterOperator;
}

export interface ScheduledSurveyAutomationPlanned {
  type: TemplateType.ScheduledSurvey;
  id: string;
  name: string;
  templateId: string | null;
  comment?: string;
  survey: OneTimeSurveyTemplate | RecurringSurveyTemplate;
  sentTime: {
    hours: number;
    minutes: number;
    offset: number;
  };
  cycleNumber: number;
  source: string;
}

export type AutomationTemplate = ScheduledMessagesTemplate;
